/**
 * The file formats a resume can be uploaded, stored and shown in.
 *
 * Resumes used to be PDF-only, so every upload accepted `application/pdf` and
 * every viewer/download hardcoded it. They can now also be a Word document or a
 * photo of a printed CV, and the type has to travel with the file: the backend
 * keeps the uploaded extension on the S3 key and serves the right
 * `Content-Type`, and everything here reads that rather than assuming.
 *
 * Must stay in step with `ALLOWED_RESUME_TYPE` in
 * `backend-api/backend/app/files/constants.py` — a format accepted here and not
 * there is a 400 the candidate cannot act on.
 */

export const RESUME_PDF_MIME = 'application/pdf';
export const RESUME_DOCX_MIME =
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

/** `accept` map for react-dropzone. */
export const RESUME_DROPZONE_ACCEPT: Record<string, string[]> = {
  [RESUME_PDF_MIME]: ['.pdf'],
  [RESUME_DOCX_MIME]: ['.docx'],
  'image/png': ['.png'],
  'image/jpeg': ['.jpg', '.jpeg'],
};

/** `accept` attribute for a bare `<input type="file">`. */
export const RESUME_INPUT_ACCEPT =
  '.pdf,.docx,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg';

/** What to tell the user they can upload. Keep the wording in one place so the
 *  dropzones, the error toasts and the empty states never disagree. */
export const RESUME_FORMATS_LABEL = 'PDF, DOCX, PNG or JPG';

/**
 * Largest resume we accept.
 *
 * Must stay in step with `MAX_RESUME_SIZE_BYTES` in
 * `backend-api/backend/app/files/constants.py` — a file this side lets through
 * and the API rejects is a failed upload the candidate cannot explain.
 */
export const MAX_RESUME_BYTES = 2 * 1024 * 1024;
export const MAX_RESUME_SIZE_LABEL = '2 MB';

const EXTENSION_BY_MIME: Record<string, string> = {
  [RESUME_PDF_MIME]: '.pdf',
  [RESUME_DOCX_MIME]: '.docx',
  'application/msword': '.doc',
  'image/png': '.png',
  'image/jpeg': '.jpg',
};

const RESUME_EXTENSIONS = ['.pdf', '.docx', '.png', '.jpg', '.jpeg'];

/** Extension of a filename or URL, lowercased, including the dot. */
export const extensionOf = (nameOrUrl?: string | null): string => {
  const path = (nameOrUrl ?? '').split(/[?#]/)[0];
  const dot = path.lastIndexOf('.');
  return dot === -1 ? '' : path.slice(dot).toLowerCase();
};

/**
 * Whether a picked file is one we can accept.
 *
 * By extension, not `file.type`: a browser reports an empty type for a DOCX
 * often enough that trusting it rejects valid resumes, and the backend
 * validates the extension too.
 */
export const isAllowedResumeFile = (file: File): boolean =>
  RESUME_EXTENSIONS.includes(extensionOf(file.name));

/**
 * The type a fetched resume actually is.
 *
 * Falls back to PDF, which is what every resume stored before this existed is —
 * so a response with no usable header renders exactly as it did before.
 */
export const resumeMimeFromResponse = (response: {
  headers?: Record<string, any> | any;
}): string => {
  const header =
    response?.headers?.['content-type'] ?? response?.headers?.['Content-Type'];
  const mime = String(header ?? '')
    .split(';')[0]
    .trim()
    .toLowerCase();
  return mime || RESUME_PDF_MIME;
};

/** The fetched resume as a Blob carrying its real type. */
export const resumeBlobFromResponse = (response: {
  data: unknown;
  headers?: Record<string, any> | any;
}): Blob =>
  new Blob([response.data as BlobPart], {
    type: resumeMimeFromResponse(response),
  });

export const isPdfResumeMime = (mime?: string | null): boolean =>
  (mime ?? RESUME_PDF_MIME) === RESUME_PDF_MIME;

export const isImageResumeMime = (mime?: string | null): boolean =>
  (mime ?? '').startsWith('image/');

export const isDocxResumeMime = (mime?: string | null): boolean =>
  mime === RESUME_DOCX_MIME;

/**
 * Download name for a resume, given what the server says it is.
 *
 * `base` is the human part ("Priya resume"); the extension comes from the type,
 * so a DOCX no longer lands in the employer's downloads folder named `.pdf`
 * where nothing will open it.
 */
export const resumeDownloadName = (
  base: string,
  mime?: string | null,
): string => `${base}${EXTENSION_BY_MIME[mime ?? RESUME_PDF_MIME] ?? '.pdf'}`;

/** Short badge label for a resume file — "pdf", "docx", "jpg". */
export const resumeFormatLabel = (mimeOrName?: string | null): string => {
  const ext = extensionOf(mimeOrName);
  if (ext) {
    return ext.slice(1);
  }
  return (EXTENSION_BY_MIME[mimeOrName ?? ''] ?? '.pdf').slice(1);
};
